"use client";
import { Modal } from "antd";
import { useEffect, useState } from "react";
import VideoPlayer from "@/components/VideoPlayerBox";
import DeliverableCard from "@/components/DeliverableCard";
import "./styles.scss";
import classNames from "classnames";

interface Deliverable {
  id: string;
  name: string;
  websiteUrl?: string;
  file?: {
    id: string;
    url: string;
    filename: string;
    type: string;
    size: number;
  }[];
  type: string;
}

export interface IProps {
  src: string;
  title: string;
  description: string;
  open: boolean;
  setOpen: (v: boolean) => void;
  deliverables?: Deliverable[];
}

export default function VideoModal(props: IProps) {
  const { src, title, description, open, setOpen, deliverables } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [showAllDeliverables, setShowAllDeliverables] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    if (open) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
    return () => {
      document.documentElement.style.overflow = "auto";
    };
  }, [open]);

  useEffect(() => {
    // 检查当前窗口大小
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    // 初始检查
    checkIsDesktop();

    // 添加窗口大小变化监听
    window.addEventListener("resize", checkIsDesktop);

    return () => {
      window.removeEventListener("resize", checkIsDesktop);
    };
  }, []);

  useEffect(() => {
    // 设置 15 秒超时
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        // message.error("加载超时，请刷新重试");
      }
    }, 15000);

    return () => clearTimeout(timeoutId);
  }, [isLoading]);

  const handleReplay = () => {
    // 每次打开模态框时重置视频
    if (open) {
      return true;
    }
    return false;
  };

  return (
    <Modal
      title=""
      open={open}
      closeIcon={null}
      styles={{
        header: {
          backgroundColor: "rgba(0, 0, 0, 0)", // 完全透明背景
        },
        // 可以同时设置内容区域样式
        content: {
          backgroundColor: "rgba(0, 0, 0, 0)",
          color: "white",
        },
      }}
      onCancel={() => setOpen(false)}
      className={"video--modal-warp"}
      footer={null}
    >
      <div className={"video--box"}>
        <div
          className="video-modal-close"
          onClick={() => {
            setOpen(false);
          }}
        >
          <i className="icon iconfont icon-a-close-line4"></i>
        </div>
        <div className={"video--box-title"}>{title}</div>
        <div className={"video--box-description"}>{description}</div>

        {/* 交付物展示区域 */}
        {deliverables && deliverables.length > 0 && (
          <div className="video--box-deliverables">
            <div
              className={classNames("deliverables-grid", {
                expanded: showAllDeliverables,
              })}
              data-count={deliverables.length}
            >
              {(() => {
                // PC端和移动端逻辑分离
                if (isDesktop) {
                  // PC端逻辑
                  if (deliverables.length >= 4) {
                    if (showAllDeliverables) {
                      // 展开状态：显示所有交付物 + 收起按钮
                      return [
                        ...deliverables.map((deliverable) => (
                          <DeliverableCard key={deliverable.id} deliverable={deliverable} />
                        )),
                        <div
                          key="collapse-toggle"
                          className="expand-toggle"
                          data-action="collapse"
                          onClick={() => setShowAllDeliverables(false)}
                        >
                          <i className="icon iconfont icon-a-arrow-right-s-line1"></i>
                          <span>收起</span>
                        </div>,
                      ];
                    } else {
                      // 收起状态：显示前2个 + 展开按钮
                      const visibleDeliverables = deliverables.slice(0, 2);
                      return [
                        ...visibleDeliverables.map((deliverable) => (
                          <DeliverableCard key={deliverable.id} deliverable={deliverable} />
                        )),
                        <div key="expand-toggle" className="expand-toggle" onClick={() => setShowAllDeliverables(true)}>
                          <i className="icon iconfont icon-a-arrow-right-s-line1"></i>
                          <span>展开全部 ({deliverables.length})</span>
                        </div>,
                      ];
                    }
                  } else {
                    // 3个或以下：直接显示所有
                    return deliverables.map((deliverable) => (
                      <DeliverableCard key={deliverable.id} deliverable={deliverable} />
                    ));
                  }
                } else {
                  // 移动端逻辑
                  if (deliverables.length >= 3) {
                    if (showAllDeliverables) {
                      // 展开状态：显示所有交付物 + 收起按钮
                      return [
                        ...deliverables.map((deliverable) => (
                          <DeliverableCard key={deliverable.id} deliverable={deliverable} />
                        )),
                        <div
                          key="collapse-toggle"
                          className="expand-toggle"
                          data-action="collapse"
                          onClick={() => setShowAllDeliverables(false)}
                        >
                          <i className="icon iconfont icon-a-arrow-right-s-line1"></i>
                          <span>收起</span>
                        </div>,
                      ];
                    } else {
                      // 收起状态：显示前2个 + 展开按钮
                      const visibleDeliverables = deliverables.slice(0, 2);
                      return [
                        ...visibleDeliverables.map((deliverable) => (
                          <DeliverableCard key={deliverable.id} deliverable={deliverable} />
                        )),
                        <div key="expand-toggle" className="expand-toggle" onClick={() => setShowAllDeliverables(true)}>
                          <i className="icon iconfont icon-a-arrow-right-s-line1"></i>
                          <span>展开全部 ({deliverables.length})</span>
                        </div>,
                      ];
                    }
                  } else {
                    // 2个或以下：直接显示所有
                    return deliverables.map((deliverable) => (
                      <DeliverableCard key={deliverable.id} deliverable={deliverable} />
                    ));
                  }
                }
              })()}
            </div>
          </div>
        )}

        <VideoPlayer videoSrc={src} autoPlay={true} onReplay={handleReplay} />
      </div>
    </Modal>
  );
}
